import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, roleLabel, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, UserCog, KeyRound, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "logistics_manager", "courier", "repassage", "tri_etiquetage"];

export default function Personnel() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank());
  const [pwDialog, setPwDialog] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  function blank() { return { name: "", email: "", password: "", phone: "", address: "", role: "logistics_manager", salary: 0, hire_date: "", active: true }; }

  const load = () => { setLoading(true); api.get("/personnel").then((r) => setStaff(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(blank()); setShowForm(true); };
  const openEdit = (s) => { setEdit(s); setForm({ ...blank(), ...s, password: "" }); setShowForm(true); };

  const save = async () => {
    try {
      const payload = { ...form, salary: Number(form.salary) || 0 };
      if (edit) {
        const { password, email, ...upd } = payload;
        await api.put(`/personnel/${edit.id}`, upd);
      } else {
        await api.post("/personnel", payload);
      }
      toast.success("Employé enregistré"); setShowForm(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const resetPw = async () => {
    try { await api.post(`/personnel/${pwDialog.id}/reset-password`, { new_password: newPw }); toast.success("Mot de passe réinitialisé"); setPwDialog(null); setNewPw(""); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/personnel/${delTarget.id}`);
      toast.success("Employé supprimé"); setDelTarget(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Personnel</h1><p className="text-slate-500 text-sm mt-1">Gestion des employés et des rôles</p></div>
        <Button data-testid="staff-new-btn" onClick={openNew} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-2" /> Nouvel employé</Button>
      </div>

      {loading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <div className="bg-white border rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Nom</th><th className="text-left px-4 py-3 hidden sm:table-cell">Email</th><th className="text-left px-4 py-3">Rôle</th><th className="text-right px-4 py-3 hidden md:table-cell">Salaire</th><th className="text-left px-4 py-3">Statut</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-t">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-semibold">{s.name?.[0]}</div>{s.name}</div></td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{s.email}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-slate-100 rounded-full px-2 py-1">{roleLabel[s.role]}</span></td>
                  <td className="px-4 py-3 text-right hidden md:table-cell">{money(s.salary)}</td>
                  <td className="px-4 py-3"><span className={`text-xs rounded-full px-2 py-0.5 ${s.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.active ? "Actif" : "Inactif"}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setPwDialog(s)} data-testid={`staff-pw-${s.id}`} className="p-1.5 text-slate-400 hover:text-amber-600"><KeyRound className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(s)} data-testid={`staff-edit-${s.id}`} className="p-1.5 text-slate-400 hover:text-brand-600"><UserCog className="w-4 h-4" /></button>
                    <button onClick={() => setDelTarget(s)} data-testid={`staff-delete-${s.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Modifier" : "Nouvel"} employé</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nom complet *</Label><Input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email *</Label><Input data-testid="pf-email" type="email" value={form.email} disabled={!!edit} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            {!edit && <div><Label>Mot de passe</Label><Input data-testid="pf-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Clinit2026!" className="mt-1" /></div>}
            <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="pf-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Salaire ($)</Label><Input data-testid="pf-salary" type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="mt-1" /></div>
            <div><Label>Date d'embauche</Label><Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Actif</label>
          </div>
          <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button data-testid="pf-submit" onClick={save} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwDialog} onOpenChange={(v) => !v && setPwDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Réinitialiser le mot de passe</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Nouveau mot de passe pour <b>{pwDialog?.name}</b></p>
          <Input data-testid="pw-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min. 6 caractères" className="mt-1" />
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setPwDialog(null)}>Annuler</Button><Button data-testid="pw-submit" onClick={resetPw} className="bg-amber-600 hover:bg-amber-700">Réinitialiser</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" /> Supprimer l'employé</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Voulez-vous vraiment supprimer <b>{delTarget?.name}</b> ({delTarget && roleLabel[delTarget.role]}) ? Son compte et son accès seront définitivement supprimés. Cette action est irréversible.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDelTarget(null)}>Annuler</Button>
            <Button data-testid="staff-delete-confirm" onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Supprimer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
